import { DraftChip } from 'components/drafts/DraftChip';
import { GLOBAL_SEARCH_ENTITY_ICON, secondaryLineClass } from 'components/global-search/entityDisplay';
import { HighlightedText } from 'components/global-search/HighlightedText';
import { isPlainLeftClick } from 'components/global-search/GlobalSearchRow';
import { CloseOutlined, Loader2Icon } from 'components/icons';
import { Badge } from 'components/ui/badge';
import { Card } from 'components/ui/card';
import { Skeleton } from 'components/ui/skeleton';
import { EndOfListSentinel } from 'components/tables/EndOfListSentinel';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { formatRelativeTimeFromNow } from 'pages/ai-chat/utils/relativeTime';
import { GlobalSearchDidYouMean, GlobalSearchRow, GlobalSearchRowCue } from 'types/globalSearch';
import { cn } from 'utils/cn';
import { pathForResult } from 'utils/globalSearchRoutes';
import { captureSearchResultClicked } from 'utils/searchAnalytics';

/** One "narrowed by" chip: a facet selection, named, with the click that takes it off again. */
export interface NarrowedByChip {
    id: string;
    label: string;
    onRemove: () => void;
}

export interface ResultCardProps {
    /** The query the rows answer, which lags what is in the box while a new one is in flight. */
    query: string;
    total: number;
    /** The match set filled the backend's cap, so `total` is a floor and is drawn as "total+". */
    capped: boolean;
    didYouMean: GlobalSearchDidYouMean | null;
    onDidYouMean: (term: string) => void;
    narrowedBy: NarrowedByChip[];
    rows: GlobalSearchRow[];
    /** The first page is still on its way; the card draws its own shape until it lands. */
    isLoading: boolean;
    isLoadingMore: boolean;
    hasNextPage: boolean;
    onLoadMore: () => void;
}

/**
 * The facet selections that are narrowing the list, each one removable.
 *
 * It is drawn beside the zero-result state as well as over the rows, because that is where it
 * matters most: a narrowing that matched nothing leaves no rail to widen it again — the backend
 * computes the facets over the match set, so an empty match set carries no facets — and the chips
 * are then the only way back that is not the browser's own Back button.
 */
export function NarrowedBy({ chips }: { chips: NarrowedByChip[] }) {
    const intl = useIntl();

    if (chips.length === 0) {
        return null;
    }

    return (
        <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-grey-500 text-xs leading-[18px]">
                {intl.formatMessage({ id: 'global-search.narrowed-by' })}
            </span>
            {chips.map((chip) => (
                <button
                    key={chip.id}
                    type="button"
                    onClick={chip.onRemove}
                    aria-label={intl.formatMessage({ id: 'global-search.remove-filter' }, { filter: chip.label })}
                    className="bg-primary-surface-subtle text-primary inline-flex items-center gap-1.5 rounded-[4px] px-2 py-px text-xs leading-5 font-semibold"
                >
                    {chip.label}
                    <CloseOutlined aria-hidden className="text-[9px]" />
                </button>
            ))}
        </div>
    );
}

/**
 * The better spelling, offered as the word itself to press.
 *
 * The heading over the rows and the card that stands in for them both make the same offer, and a
 * correction worded or styled one way in one of them and another way in the other would read as two
 * different things. What surrounds it is each caller's: a separator here, the count of what the
 * better spelling finds there.
 */
export function DidYouMean({ term, onSelect }: { term: string; onSelect: (term: string) => void }) {
    return (
        <FormattedMessage
            id="global-search.did-you-mean"
            values={{
                term: (
                    <button
                        key="term"
                        type="button"
                        className="text-link hover:underline"
                        onClick={() => onSelect(term)}
                    >
                        {term}
                    </button>
                ),
            }}
        />
    );
}

/**
 * The ranked list, under the heading that says what it answers.
 *
 * One list, not one section per family: the families are blended by rank and the glyph plus the
 * facts line are what say which family a row belongs to. The top row carries the accent — it is the
 * best answer, not a separate kind of row.
 */
export function ResultCard({
    query,
    total,
    capped,
    didYouMean,
    onDidYouMean,
    narrowedBy,
    rows,
    isLoading,
    isLoadingMore,
    hasNextPage,
    onLoadMore,
}: ResultCardProps) {
    const intl = useIntl();
    const navigate = useNavigate();

    const open = (row: GlobalSearchRow, position: number) => {
        captureSearchResultClicked({
            surface: 'results-page',
            entityType: row.entityType,
            position,
            bestBet: false,
        });
        navigate(pathForResult(row.entityType, row.id));
    };

    return (
        <Card className="min-w-0 flex-1 gap-0 rounded-[20px] py-0">
            <div className="px-6 pt-5 pb-4">
                <h1 className="text-h4 text-grey-700 m-0 mb-1.5 font-semibold">
                    {intl.formatMessage({ id: 'global-search.results-for' }, { query })}
                </h1>
                <div className="text-grey-500 text-xs leading-[18px]">
                    {!isLoading && (
                        <>
                            {intl.formatMessage(
                                { id: capped ? 'global-search.results-count-capped' : 'global-search.results-count' },
                                { count: total }
                            )}
                            {didYouMean !== null && (
                                <>
                                    {' · '}
                                    <DidYouMean term={didYouMean.term} onSelect={onDidYouMean} />
                                </>
                            )}
                        </>
                    )}
                </div>
                <NarrowedBy chips={narrowedBy} />
            </div>

            {isLoading ? (
                <ResultsSkeleton />
            ) : (
                rows.map((row, position) => (
                    <a
                        key={`${row.entityType}-${row.id}-${position}`}
                        href={pathForResult(row.entityType, row.id)}
                        onClick={(event) => {
                            if (!isPlainLeftClick(event)) {
                                return;
                            }
                            event.preventDefault();
                            open(row, position);
                        }}
                        className={cn(
                            'border-divider hover:bg-grey-50 flex gap-3.5 border-t px-6 py-3.5 no-underline',
                            position === 0 && 'border-l-primary bg-primary-surface-subtle border-l-[3px] pl-[21px]'
                        )}
                    >
                        <ResultIcon row={row} />
                        <span className="min-w-0 flex-1">
                            <span className="text-link flex items-center gap-1.5 text-sm leading-[22px] font-semibold">
                                <span className="truncate">
                                    <HighlightedText text={row.displayName} />
                                </span>
                                {row.isDraft ? <DraftChip className="flex-none" /> : null}
                            </span>
                            {row.secondaryLine !== null && (
                                <span
                                    className={cn(
                                        'mt-0.5 block text-xs leading-[18px]',
                                        secondaryLineClass(row.secondaryLineKind)
                                    )}
                                >
                                    <HighlightedText text={row.secondaryLine} />
                                </span>
                            )}
                        </span>
                        <span className="w-[132px] flex-none text-right">
                            <RowCue cue={row.cue} />
                            <span className="text-grey-600 block text-xs leading-[18px]">
                                {intl.formatMessage(
                                    { id: 'global-search.updated-ago' },
                                    { ago: formatRelativeTimeFromNow(intl, new Date(row.updatedAt).getTime()) }
                                )}
                            </span>
                        </span>
                    </a>
                ))
            )}

            {isLoadingMore && (
                <div className="border-divider bg-grey-50 text-grey-600 flex items-center justify-center gap-2.5 border-t px-6 py-5 text-xs leading-[18px]">
                    <Loader2Icon aria-hidden className="text-primary size-4 animate-spin" />
                    {intl.formatMessage({ id: 'global-search.loading-more' })}
                </div>
            )}
            <EndOfListSentinel
                rowCount={rows.length}
                hasNextPage={hasNextPage}
                isLoadingMore={isLoadingMore}
                onLoadMore={onLoadMore}
            />
        </Card>
    );
}

function ResultIcon({ row }: { row: GlobalSearchRow }) {
    const Icon = GLOBAL_SEARCH_ENTITY_ICON[row.entityType];
    return (
        <span className="bg-primary-surface text-primary flex size-7 flex-none items-center justify-center rounded-[8px]">
            <Icon aria-hidden className="text-[16px]" />
        </span>
    );
}

/**
 * Why this row is here, when there is a reason worth the space.
 *
 * A relationship to the record is a claim about the reader and is drawn as a pill; a partial term
 * match is a caveat about the match and is drawn as plain text, so the two never read as the same
 * kind of statement.
 */
function RowCue({ cue }: { cue: GlobalSearchRowCue | null }) {
    const intl = useIntl();
    if (cue === null) {
        return null;
    }
    if (cue.type === 'PARTIAL_TERM_MATCH') {
        if (cue.matchedTerms === null || cue.totalTerms === null) {
            return null;
        }
        return (
            <span className="text-grey-500 new:text-caption mb-0.5 block text-[11px] leading-4">
                {intl.formatMessage(
                    { id: 'global-search.cue.partial-terms' },
                    { matched: cue.matchedTerms, total: cue.totalTerms }
                )}
            </span>
        );
    }
    return (
        <Badge className="bg-primary-surface text-primary new:text-caption mb-0.5 h-auto rounded-[8px] px-2 py-px text-[11px] leading-4 font-semibold">
            {intl.formatMessage({
                id: cue.type === 'RESPONSIBLE' ? 'global-search.cue.responsible' : 'global-search.cue.involved',
            })}
        </Badge>
    );
}

/** The shape of the answer while the first page is in flight — the row rhythm, not a spinner. */
function ResultsSkeleton() {
    return (
        <div aria-hidden>
            {['w-[58%]', 'w-[44%]', 'w-[66%]', 'w-[50%]', 'w-[62%]'].map((width) => (
                <div key={width} className="border-divider flex gap-3.5 border-t px-6 py-3.5">
                    <Skeleton className="bg-grey-200 size-7 flex-none rounded-[8px]" />
                    <div className="flex min-w-0 flex-1 flex-col gap-2 pt-1">
                        <Skeleton className={cn('bg-grey-200 h-3.5 rounded-[4px]', width)} />
                        <Skeleton className="bg-grey-100 h-2.5 w-[80%] rounded-[4px]" />
                    </div>
                    <Skeleton className="bg-grey-100 h-2.5 w-[88px] flex-none rounded-[4px]" />
                </div>
            ))}
        </div>
    );
}
